import MiniSearch, {SearchOptions, SearchResult as MiniSearchResult} from "minisearch";

/**
 * A heading within a search index entry.
 */
export interface SearchHeading {
    anchor: string;
    text: string;
    level: number;
}

/**
 * A single entry in the search index.
 */
export interface SearchEntry {
    url: string;
    title: string | null;
    description: string | null;
    keywords: string[];
    content: string;
    headings: SearchHeading[];
}

/**
 * The search index structure as generated by Quarkdown.
 */
export interface SearchIndex {
    entries: SearchEntry[];
}

/**
 * Internal document structure used for indexing.
 */
interface IndexedDocument {
    id: number;
    url: string;
    title: string;
    description: string;
    keywords: string;
    content: string;
    headings: string;
}

/**
 * A single search result.
 */
export interface DocumentSearchResult {
    entry: SearchEntry;
    score: number;
    matchedTerms: string[];
    /** Map of matched terms to the fields they were found in */
    matchedFields: Record<string, string[]>;
}

/**
 * Options for the document search engine.
 */
export interface DocumentSearchOptions {
    /** Enable fuzzy matching */
    fuzzy?: boolean | number;
    /** Enable prefix matching */
    prefix?: boolean;
    /** Boost factor for title matches */
    titleBoost?: number;
    /** Boost factor for heading matches */
    headingsBoost?: number;
    /** Boost factor for keywords matches */
    keywordsBoost?: number;
    /** Boost factor for description matches */
    descriptionBoost?: number;
    /** Boost factor for content matches */
    contentBoost?: number;
    /** Maximum number of results to return */
    maxResults?: number;
}

const DEFAULT_OPTIONS: Required<Omit<DocumentSearchOptions, "maxResults">> = {
    fuzzy: 0.2,
    prefix: true,
    titleBoost: 3,
    headingsBoost: 2,
    keywordsBoost: 2.5,
    descriptionBoost: 1.5,
    contentBoost: 1,
};

/**
 * A DOM-independent search engine for Quarkdown documents.
 * Uses MiniSearch for fuzzy full-text search.
 */
export class DocumentSearch {
    private miniSearch: MiniSearch<IndexedDocument>;
    private entriesMap: Map<number, SearchEntry>;
    private options: Required<Omit<DocumentSearchOptions, "maxResults">> & Pick<DocumentSearchOptions, "maxResults">;

    /**
     * Creates a new DocumentSearch instance.
     *
     * @param index - The search index to use
     * @param options - Search configuration options
     */
    constructor(index: SearchIndex, options: DocumentSearchOptions = {}) {
        this.options = { ...DEFAULT_OPTIONS, ...options };
        this.entriesMap = new Map();
        this.miniSearch = this.createMiniSearch();
        this.indexEntries(index.entries);
    }

    private createMiniSearch(): MiniSearch<IndexedDocument> {
        return new MiniSearch<IndexedDocument>({
            fields: ["title", "description", "keywords", "content", "headings"],
            storeFields: ["url"],
            searchOptions: {
                boost: {
                    title: this.options.titleBoost,
                    headings: this.options.headingsBoost,
                    keywords: this.options.keywordsBoost,
                    description: this.options.descriptionBoost,
                    content: this.options.contentBoost,
                },
                fuzzy: this.options.fuzzy,
                prefix: this.options.prefix,
            },
        });
    }

    private indexEntries(entries: SearchEntry[]): void {
        const documents: IndexedDocument[] = entries.map((entry, index) => {
            this.entriesMap.set(index, entry);
            return {
                id: index,
                url: entry.url,
                title: entry.title ?? "",
                description: entry.description ?? "",
                keywords: entry.keywords.join(" "),
                content: entry.content,
                headings: entry.headings.map((h) => h.text).join(" "),
            };
        });

        this.miniSearch.addAll(documents);
    }

    /**
     * Performs a fuzzy search on the index.
     *
     * @param query - The search query string
     * @param searchOptions - Optional per-search options to override defaults
     * @returns An array of search results sorted by relevance score
     */
    search(query: string, searchOptions?: SearchOptions): DocumentSearchResult[] {
        if (!query.trim()) {
            return [];
        }

        const results: MiniSearchResult[] = this.miniSearch.search(query, searchOptions);
        const seenUrls = new Set<string>();
        const mappedResults: DocumentSearchResult[] = results
            .map((result) => {
                const entry = this.entriesMap.get(result.id as number);
                if (!entry) return null;
                if (seenUrls.has(entry.url)) return null;
                seenUrls.add(entry.url);

                return {
                    entry,
                    score: result.score,
                    matchedTerms: result.terms,
                    matchedFields: result.match,
                };
            })
            .filter((result): result is DocumentSearchResult => result !== null);

        if (this.options.maxResults !== undefined) {
            return mappedResults.slice(0, this.options.maxResults);
        }

        return mappedResults;
    }

    /**
     * Gets autocomplete suggestions for a partial query.
     *
     * @param query - The partial search query
     * @param maxSuggestions - Maximum number of suggestions to return. Default: 5
     * @returns An array of suggested search terms
     */
    suggest(query: string, maxSuggestions: number = 5): string[] {
        if (!query.trim()) {
            return [];
        }

        return this.miniSearch
            .autoSuggest(query, { fuzzy: this.options.fuzzy, prefix: this.options.prefix })
            .slice(0, maxSuggestions)
            .map((suggestion) => suggestion.suggestion);
    }

    /**
     * Returns the number of indexed entries.
     */
    get entryCount(): number {
        return this.miniSearch.documentCount;
    }
}

/**
 * Creates a DocumentSearch instance from a search index.
 *
 * @param index - The search index object or JSON string
 * @param options - Search configuration options
 * @returns A configured DocumentSearch instance
 */
export function createSearch(index: SearchIndex | string, options?: DocumentSearchOptions): DocumentSearch {
    const parsedIndex: SearchIndex = typeof index === "string" ? JSON.parse(index) : index;
    return new DocumentSearch(parsedIndex, options);
}
